import { IfAbility } from '@efdigital/ll-addon-auth';
import type { CoachingPartnership } from '@efdigital/ll-addon-data/types/coaching/coaching-partnership';
import type { CoachMatch } from '@efdigital/ll-admin-v2/data/builders/coaching/admin-ai-matching';
import { AdminSessionsRequestError } from '@efdigital/ll-admin-v2/data/builders/coaching/admin-sessions';
import type AdminAiMatchingService from '@efdigital/ll-admin-v2/services/admin-ai-matching';
import type UserLookupService from '@efdigital/ll-admin-v2/services/user-lookup';
import { resolveCoachNames } from '@efdigital/ll-admin-v2/utils/coaching/coach-display-name';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

const STATUS_CLASS: Record<string, string> = {
  pending: 'text-bg-secondary',
  chemistry: 'text-bg-info',
  declined: 'text-bg-warning',
  chosen: 'text-bg-success',
  reassigned: 'text-bg-secondary',
};

type RankedCoachRow = {
  match: CoachMatch;
  name: string;
  partnership?: CoachingPartnership;
};

type MatchesLoad = {
  rows: RankedCoachRow[];
  errorMessage: string | null;
};

function matchesErrorCopy(error: unknown): string {
  if (error instanceof AdminSessionsRequestError) {
    const message = error.message.toLowerCase();
    if (message.includes('no onboarding answer')) {
      return 'This coachee has not completed the matching questionnaire yet.';
    }
    if (message.includes('no vector found')) {
      return 'Match scores are still being calculated. Try again shortly.';
    }
    if (
      message.includes('coaching is not enabled') ||
      message.includes('missing an onboarding quiz') ||
      message.includes('not categorised')
    ) {
      return 'Coach matching is not set up for this cohort.';
    }
  }
  return "Couldn't load best-matched coaches.";
}

interface BestMatchedCoachesSignature {
  Args: {
    cohortId: string;
    coacheeId: string;
    currentCoachUserId: string;
    partnerships: CoachingPartnership[];
    /** Bumped after assign/reassign so the list reloads. */
    reloadToken: number;
    onAssign: (coachUserId: string) => void;
  };
}

/**
 * Ranked coach list for one coachee — the same payload the coachee sees, with
 * admin assign actions. Loads lazily when the parent row is expanded.
 */
export class BestMatchedCoaches extends Component<BestMatchedCoachesSignature> {
  @service declare store: Store;
  @service declare userLookup: UserLookupService;
  @service declare adminAiMatching: AdminAiMatchingService;

  loadMatches = async (): Promise<MatchesLoad> => {
    // The token is read so this re-runs after an assign/reassign.
    void this.args.reloadToken;
    try {
      const matches = await this.adminAiMatching.getCoachMatches(
        this.args.cohortId,
        this.args.coacheeId
      );
      const sorted = [...matches].sort((a, b) => b.relevance - a.relevance);
      const names = await resolveCoachNames(
        this.store,
        this.userLookup,
        sorted.map((match) => match.coachId)
      );
      return {
        rows: sorted.map((match) => ({
          match,
          name: names[match.coachId] ?? match.coachId,
          partnership: this.args.partnerships.find(
            (partnership) => partnership.coachUserId === match.coachId
          ),
        })),
        errorMessage: null,
      };
    } catch (error) {
      return { rows: [], errorMessage: matchesErrorCopy(error) };
    }
  };

  roundedRelevance = (relevance: number): number => Math.round(relevance);

  statusClass = (status: string): string =>
    STATUS_CLASS[status] ?? 'text-bg-secondary';

  canAssign = (coachUserId: string): boolean =>
    coachUserId !== this.args.currentCoachUserId;

  <template>
    <div class="mt-3" data-test-best-matched-coaches>
      <h3 class="h6 mb-2">Best-matched coaches</h3>
      <Await @promise={{(this.loadMatches)}}>
        <:pending>
          <p class="text-muted small mb-0">Loading matches…</p>
        </:pending>
        <:success as |result|>
          {{#if result.errorMessage}}
            <p class="text-muted small mb-0">{{result.errorMessage}}</p>
          {{else if result.rows.length}}
            <div class="table-responsive">
              <table class="table table-sm align-middle mb-0">
                <caption class="visually-hidden">Coaches ranked by relevance to
                  this coachee</caption>
                <thead>
                  <tr>
                    <th scope="col">Coach</th>
                    <th scope="col">Match</th>
                    <th scope="col">Status</th>
                    <th scope="col"><span
                        class="visually-hidden"
                      >Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {{#each result.rows as |row|}}
                    <tr data-test-best-matched-coach={{row.match.coachId}}>
                      <td class="fw-medium">{{row.name}}</td>
                      <td>{{this.roundedRelevance row.match.relevance}}%</td>
                      <td>
                        {{#if row.partnership}}
                          <span
                            class="badge
                              {{this.statusClass row.partnership.status}}"
                          >{{row.partnership.status}}</span>
                        {{else}}
                          <span class="text-muted">—</span>
                        {{/if}}
                      </td>
                      <td class="text-end">
                        {{#if (this.canAssign row.match.coachId)}}
                          <IfAbility
                            @ability="coaching-partnerships"
                            @atLeast="editor"
                          >
                            <:if>
                              <button
                                type="button"
                                class="btn btn-sm btn-outline-primary"
                                data-test-assign-matched-coach
                                {{on "click" (fn @onAssign row.match.coachId)}}
                              >Assign</button>
                            </:if>
                          </IfAbility>
                        {{/if}}
                      </td>
                    </tr>
                  {{/each}}
                </tbody>
              </table>
            </div>
          {{else}}
            <p class="text-muted small mb-0">No matched coaches yet. The cohort
              pool may be empty, or no coach has linked a calendar.</p>
          {{/if}}
        </:success>
        <:error>
          <p class="text-danger small mb-0">Couldn't load best-matched coaches.</p>
        </:error>
      </Await>
    </div>
  </template>
}
